import React, { useState, useEffect } from 'react';
import { X, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LocationFormModal = ({ editingLocation, onSave, onCancel, organizationActivityTypes = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discipline: 'nsf',
    active: true,
    supportedActivityTypes: [] // New field for activity types
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedQrId, setGeneratedQrId] = useState('');
  const [showAllDisciplines, setShowAllDisciplines] = useState(false);

  // Bransje opsjoner med farger - top 3 mest brukte
  const primaryDisciplines = [
    { 
      id: 'nsf', 
      name: 'NSF (Norges Sportskytterforbund)', 
      color: '#FFD700', // Gul
      prefix: 'nsf-'
    },
    { 
      id: 'dfs', 
      name: 'DFS (Det Frivillige Skyttervesen)', 
      color: '#10B981', // Grønn
      prefix: 'dfs-'
    },
    { 
      id: 'dssn', 
      name: 'DSSN (Dynamisk Sportsskyting Norge)', 
      color: '#3B82F6', // Blå
      prefix: 'dssn-'
    }
  ];

  // Resterende 8 skytedisipliner
  const secondaryDisciplines = [
    { 
      id: 'sws', 
      name: 'SWS (Scandinavian Western Shooters)', 
      color: '#F59E0B', // Oransje
      prefix: 'sws-'
    },
    { 
      id: 'nsu', 
      name: 'NSU (Norsk Svartkruttunion)', 
      color: '#6B7280', // Grå
      prefix: 'nsu-'
    },
    { 
      id: 'nrof', 
      name: 'NROF (Norges Reserveoffiserers Forbund)', 
      color: '#6366F1', // Indigo
      prefix: 'nrof-'
    },
    { 
      id: 'nmsf', 
      name: 'NMSF (Norges Metallsilhuett Forbund)', 
      color: '#EC4899', // Rosa
      prefix: 'nmsf-'
    },
    { 
      id: 'njff', 
      name: 'NJFF (Norges Jeger- og Fiskerforbund)', 
      color: '#059669', // Emerald
      prefix: 'njff-'
    },
    { 
      id: 'nbif', 
      name: 'NBIF (Norges Bedriftsidrettsforbund)', 
      color: '#06B6D4', // Cyan
      prefix: 'nbif-'
    },
    { 
      id: 'nbsf', 
      name: 'NBSF (Norges Benkeskytterforbund)', 
      color: '#EF4444', // Rød
      prefix: 'nbsf-'
    },
    { 
      id: 'nssf', 
      name: 'NSSF (Norges skiskytterforbund)', 
      color: '#14B8A6', // Teal
      prefix: 'nssf-'
    }
  ];

  const disciplineOptions = [...primaryDisciplines, ...secondaryDisciplines];

  // Populer form hvis vi redigerer
  useEffect(() => {
    if (editingLocation) {
      setFormData({
        name: editingLocation.name || '',
        description: editingLocation.description || '',
        discipline: editingLocation.discipline || 'nsf',
        active: editingLocation.active !== false,
        supportedActivityTypes: editingLocation.supported_activity_types || []
      });
      setGeneratedQrId(editingLocation.qr_code_data || '');
    } else {
      // Reset for ny skytebane
      setFormData({
        name: '',
        description: '',
        discipline: 'nsf',
        active: true,
        supportedActivityTypes: []
      });
      generateQrId('nsf', '');
    }
  }, [editingLocation]);

  // Generer QR ID automatisk basert på navn og bransje
  const generateQrId = (discipline = formData.discipline, name = formData.name) => {
    const prefix = disciplineOptions.find(d => d.id === discipline)?.prefix || 'nsf-';
    const normalizedName = name
      .toLowerCase()
      .replace(/[^a-zæøå0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const randomSuffix = Math.random().toString(36).substring(2, 6);
    const qrId = `${prefix}${normalizedName || 'skytebane'}-${randomSuffix}`;
    setGeneratedQrId(qrId);
    return qrId;
  };

  // Håndter endringer i navn eller bransje
  useEffect(() => {
    if (!editingLocation && formData.name) {
      generateQrId(formData.discipline, formData.name);
    }
  }, [formData.name, formData.discipline, editingLocation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Navn på skytebane er påkrevd');
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedDiscipline = disciplineOptions.find(d => d.id === formData.discipline);
      const locationData = {
        name: formData.name.trim(),
        discipline: formData.discipline,
        discipline_color: selectedDiscipline.color,
        qr_code_data: generatedQrId,
        active: formData.active,
        supported_activity_types: formData.supportedActivityTypes
      };

      onSave(locationData);
    } catch (error) {
      console.error('Feil ved lagring av skytebane:', error);
      alert('Kunne ikke lagre skytebane. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyQrId = () => {
    navigator.clipboard.writeText(generatedQrId);
    alert('QR-kode ID kopiert!');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-md mx-auto flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {editingLocation ? 'Rediger skytebane' : 'Ny skytebane'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <form onSubmit={handleSubmit} id="location-form">
          {/* Navn på skytebane */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Navn på skytebane *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="F.eks. Innendørs 25m"
              required
            />
          </div>

          {/* QR-kode ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              QR-kode ID *
            </label>
            <div className="flex">
              <input
                type="text"
                value={generatedQrId}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-l-lg text-white font-mono text-sm"
              />
              <button
                type="button"
                onClick={copyQrId}
                className="px-3 py-2 bg-gray-600 border border-gray-600 rounded-r-lg text-gray-300 hover:bg-gray-500"
                title="Kopier QR-kode ID"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center mt-2">
              <button
                type="button"
                onClick={() => generateQrId()}
                className="text-sm text-yellow-400 hover:text-yellow-300 underline"
              >
                Generer automatisk
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Koden starter med disiplin ({disciplineOptions.map(d => d.prefix).join(', ')}) og brukes til å generere QR-koder
            </p>
          </div>

          {/* Aktiv skytebane */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="mr-2"
              />
              <span className="text-gray-300">Aktiv skytebane</span>
            </label>
            <p className="text-xs text-gray-400 mt-1">
              Kun aktive skytebaner kan brukes for registrering
            </p>
          </div>

          {/* Støttede skytedisipliner */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Støttede skytedisipliner
            </label>
            <div className="space-y-2">
              {/* Primary disciplines - always visible */}
              {primaryDisciplines.map((discipline) => (
                <label key={discipline.id} className="flex items-center">
                  <input
                    type="radio"
                    name="discipline"
                    value={discipline.id}
                    checked={formData.discipline === discipline.id}
                    onChange={(e) => setFormData({ ...formData, discipline: e.target.value })}
                    className="mr-3"
                  />
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded mr-2"
                      style={{ backgroundColor: discipline.color }}
                    ></div>
                    <span className="text-gray-300">{discipline.name}</span>
                  </div>
                </label>
              ))}
              
              {/* Expand/Collapse button */}
              <button
                type="button"
                onClick={() => setShowAllDisciplines(!showAllDisciplines)}
                className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 transition-colors py-1"
              >
                {showAllDisciplines ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Skjul andre disipliner
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Vis flere disipliner ({secondaryDisciplines.length})
                  </>
                )}
              </button>
              
              {/* Secondary disciplines - collapsible */}
              {showAllDisciplines && (
                <div className="space-y-2 pt-2 border-t border-gray-700">
                  {secondaryDisciplines.map((discipline) => (
                    <label key={discipline.id} className="flex items-center">
                      <input
                        type="radio"
                        name="discipline"
                        value={discipline.id}
                        checked={formData.discipline === discipline.id}
                        onChange={(e) => setFormData({ ...formData, discipline: e.target.value })}
                        className="mr-3"
                      />
                      <div className="flex items-center">
                        <div 
                          className="w-4 h-4 rounded mr-2"
                          style={{ backgroundColor: discipline.color }}
                        ></div>
                        <span className="text-gray-300">{discipline.name}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Velg hvilke skytedisipliner som kan utføres på denne banen
            </p>
          </div>

          {/* Støttede aktivitetstyper */}
          {organizationActivityTypes.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Støttede aktivitetstyper (valgfritt)
              </label>
              <div className="flex flex-wrap gap-2">
                {organizationActivityTypes.map((activityType, index) => {
                  const isSelected = formData.supportedActivityTypes.includes(activityType);
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          // Remove from selection
                          setFormData({
                            ...formData,
                            supportedActivityTypes: formData.supportedActivityTypes.filter(a => a !== activityType)
                          });
                        } else {
                          // Add to selection
                          setFormData({
                            ...formData,
                            supportedActivityTypes: [...formData.supportedActivityTypes, activityType]
                          });
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        isSelected 
                          ? 'bg-yellow-500 text-gray-900' 
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {activityType}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Velg hvilke aktivitetstyper som kan registreres på denne banen. Hvis ingen er valgt, tillates alle aktivitetstyper.
              </p>
            </div>
          )}

          </form>
        </div>
        
        {/* Footer - Fixed */}
        <div className="p-6 border-t border-gray-700">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              form="location-form"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Lagrer...' : (editingLocation ? 'Oppdater skytebane' : 'Legg til skytebane')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationFormModal;